const BASE_PREFIX = 'wlsy'

const getOriginStorage = (): Storage | undefined => {
  if (typeof window === 'undefined') return undefined
  try {
    return window.localStorage
  } catch (e) {
    console.info('Error getting window.localStorage storage', e)
  }
}

export const listAllKeys = () => {
  const results = new Set<string>()
  const originStorage = getOriginStorage()
  if (!originStorage) return []

  for (let i = 0; i < originStorage?.length; i += 1) {
    const k = originStorage?.key(i)
    if (typeof k === 'string') results.add(k)
  }

  return Array.from(results)
}

export type StorageScope = 'user'

export const localStorage = (scopePrefix?: StorageScope) => {
  const fullPrefix = `${BASE_PREFIX}${scopePrefix ? `:${scopePrefix}` : ''}`
  const withPrefix = (key: string) => `${fullPrefix}:${key}`

  const scopeKeys = () =>
    listAllKeys().filter((k) => k.startsWith(`${fullPrefix}:`))

  const stripPrefix = (key: string) => key.slice(fullPrefix.length + 1)
  const originStorage = getOriginStorage()

  const getItem = (key: string) => {
    try {
      return originStorage?.getItem(withPrefix(key))
    } catch {
      return null
    }
  }

  const setItem = (key: string, value: string) => {
    try {
      originStorage?.setItem(withPrefix(key), String(value))
    } catch {
      return
    }
  }

  const removeItem = (key: string) => {
    try {
      originStorage?.removeItem(withPrefix(key))
    } catch {
      return
    }
  }

  const clear = () => {
    try {
      scopeKeys().forEach((k) => originStorage?.removeItem(k))
    } catch {
      return
    }
  }

  const keys = () => scopeKeys().map(stripPrefix)

  const has = (key: string) => getItem(key) !== null

  const getJSON = <T = unknown>(key: string): T | null => {
    const raw = getItem(key)
    if (raw === null || raw === undefined) return null
    try {
      return JSON.parse(raw) as T
    } catch {
      return null
    }
  }

  const setJSON = (key: string, value: unknown) => {
    try {
      setItem(key, JSON.stringify(value))
    } catch {
      return
    }
  }

  return {
    getItem,
    setItem,
    removeItem,
    clear,
    keys,
    has,
    getJSON,
    setJSON,
  }
}
